import React from 'react';
import { useTranslation } from 'react-i18next';
import { BombiixModal } from '../../../Components/BombiixModal';
import { NewCheckbox } from '../../../Components/PIM/common/NewCheckbox';
import { NewSelectMultiSelect } from '../../../Components/PIM/common/NewSelectMultiSelect';
import type { NewSelectMultiSelectOption } from '../../../Components/PIM/common/NewSelectMultiSelect/NewSelectMultiSelect';
import { AssignmentDraft, AssignmentField as AssignmentFieldName, AssignmentValue, toPickedValues } from './user-assignment.types';
import { filterSubBrandsByBrand, type SubBrandOption } from './user-assignment.types';

interface AssignmentFieldProps {
  label: string;
  allLabel: string;
  searchPlaceholder: string;
  chipVariant: string;
  options: NewSelectMultiSelectOption[] | undefined;
  value: AssignmentValue | undefined;
  onChange: (next: AssignmentValue) => void;
}

interface GlobalAssignmentModalProps {
  show: boolean;
  draft: AssignmentDraft | null | undefined;
  selectedCount: number;
  brandOptions: NewSelectMultiSelectOption[] | undefined;
  subBrandOptions: SubBrandOption[] | undefined;
  category1Options: NewSelectMultiSelectOption[] | undefined;
  category2Options: NewSelectMultiSelectOption[] | undefined;
  onChange: (field: AssignmentFieldName, next: AssignmentValue) => void;
  onSave: () => void;
  onClose: () => void;
  saveDisabled?: boolean;
}

/**
 * One assignment field — the all-access tick and the value picker, laid out the
 * same way as the grid's assignment cell so the bulk editor reads as the same
 * control the row uses. Ticking all-access collapses the picker: the rights
 * payload carries all-access as the sentinel `true`, which is a different thing
 * from every option being picked, so the select's own in-menu "Select All" is
 * deliberately not used here. With all-access on there is no option to show as
 * selected, so the control displays one standing chip instead, the same way the
 * grid's assignment cell does.
 * @param {object} props - Component props.
 * @returns {React.ReactElement} The field.
 */
function AssignmentField({ label, allLabel, searchPlaceholder, chipVariant, options, value, onChange }: AssignmentFieldProps) {
  const isAll = value === true;
  const selected = (options ?? []).filter((option) => (Array.isArray(value) ? value.map(String).includes(String(option?.value)) : false));
  const shown = isAll ? [{ label: allLabel, value: '__all__' }] : selected;

  return (
    <div className="global-assignment__field">
      <NewSelectMultiSelect
        isMulti
        label={label}
        options={isAll ? [] : options ?? []}
        value={shown}
        isDisabled={isAll}
        placeholder={label}
        showSearch
        searchPlaceholder={searchPlaceholder}
        containerClassName={`user-assignment-select user-assignment-select--${chipVariant}`}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        onChange={(picked) => onChange(toPickedValues(picked))}
      />
      <label className="global-assignment__all">
        <NewCheckbox
          checked={isAll}
          aria-label={allLabel}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked ? true : [])}
        />
        <span className="global-assignment__all-text">{allLabel}</span>
      </label>
    </div>
  );
}

/**
 * Bulk assignment editor for the users grid — the floating bar's gear opens it.
 *
 * It writes one brand / sub-brand / category grant across the ticked rows in a
 * single pass, replacing whatever those users held; the caller guarantees a
 * non-empty selection by disabling the gear until a row is ticked.
 * @param {object} props - Component props.
 * @returns {React.ReactElement} The modal.
 */
function GlobalAssignmentModal({
  show,
  draft,
  selectedCount,
  brandOptions,
  subBrandOptions,
  category1Options,
  category2Options,
  onChange,
  onSave,
  onClose,
  saveDisabled = false,
}: GlobalAssignmentModalProps) {
  const { t } = useTranslation('common');
  const value: AssignmentDraft = draft ?? {};

  return (
    <BombiixModal
      show={show}
      title={t('USER_TABLE.GLOBAL_ASSIGNMENT_TITLE')}
      saveLabel={t('USER_TABLE.APPLY')}
      onSave={onSave}
      saveDisabled={saveDisabled}
      onClose={onClose}
      enforceFocus={false}
      className="global-assignment-modal"
    >
      <p className="global-assignment__hint">{t('USER_TABLE.GLOBAL_ASSIGNMENT_HINT', { count: selectedCount })}</p>
      <AssignmentField
        label={t('USER_TABLE.BRAND')}
        allLabel={t('USER_TABLE.FULL_ACCESS')}
        searchPlaceholder={t('COMMON_LABEL.SEARCH')}
        chipVariant="brand"
        options={brandOptions}
        value={value.brand}
        onChange={(next) => onChange('brand', next)}
      />
      <AssignmentField
        label={t('USER_TABLE.SUB_BRAND')}
        allLabel={t('USER_TABLE.FULL_ACCESS')}
        searchPlaceholder={t('COMMON_LABEL.SEARCH')}
        chipVariant="sub-brand"
        options={filterSubBrandsByBrand(subBrandOptions, value.brand)}
        value={value.subBrand}
        onChange={(next) => onChange('subBrand', next)}
      />
      <AssignmentField
        label={t('USER_TABLE.CATEGORY_1')}
        allLabel={t('USER_TABLE.FULL_ACCESS')}
        searchPlaceholder={t('COMMON_LABEL.SEARCH')}
        chipVariant="category"
        options={category1Options}
        value={value.category1}
        onChange={(next) => onChange('category1', next)}
      />
      <AssignmentField
        label={t('USER_TABLE.CATEGORY_2')}
        allLabel={t('USER_TABLE.FULL_ACCESS')}
        searchPlaceholder={t('COMMON_LABEL.SEARCH')}
        chipVariant="category-2"
        options={category2Options}
        value={value.category2}
        onChange={(next) => onChange('category2', next)}
      />
    </BombiixModal>
  );
}

export default GlobalAssignmentModal;
